import React, { ReactText, lazy, useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import SearchField from 'shared/components/SearchField'
import { ParticipantRowItem, getEmptyExportFilter, ParticipantExportType } from 'pages/Participants/model/participant'
import history from 'shared/utils/history'
import upperFirst from 'lodash/upperFirst'
import upperCase from 'lodash/upperCase'
import omit from 'lodash/omit'
import map from 'lodash/map'
import keys from 'lodash/keys'
import { ColumnSort } from 'shared/types'
import Tabs from 'shared/components/Tabs'
import useRouter from 'shared/hooks/useRouter'
import usePageTable from 'shared/hooks/usePageTable'
import { useDispatch, useSelector } from 'react-redux'
import { ExportAllUserType } from 'shared/redux/programs/reducer'
import { showSuccess, showDialog, hideDialog, showError, Screening, UserScreening } from 'shared/redux/app/reducer'
import ConfirmDialog from 'shared/components/ConfirmDialog'
import FlaggedTable from 'pages/Flagged/components/FlaggedTable'
import ExportDialog from 'pages/Participants/components/Dialogs/ExportDialog'
import { Header, PageContainer, Title } from 'shared/components/StyledComponents'
import { ParentTypeWithStatus } from 'pages/Parent/model/parent'
import AddIcon from 'assets/icons/add.svg'
import Box from 'shared/components/Box'
import SvgExportIcon from 'assets/icons/Export'
import {
  permissionSelector,
  roleSelector,
  authSelector,
  screeningSelector,
  organizationSettingSelector,
} from 'shared/hocs/withAuth'

import ConfirmationDialog from 'shared/components/Screening/components/Dialogs/ConfirmationDialog'
import BasicDialog from 'shared/components/Screening/components/Dialogs/BasicDialog'
import FormDialog from 'shared/components/Screening/components/Dialogs/FormDialog'
import MultiUserFormDialog from 'shared/components/Screening/components/Dialogs/MultiUserFormDialog'
import FollowupDialog from 'shared/components/Screening/components/Dialogs/FollowupDialog'
import useMutation, { useCreateNode, useDeleteNode, useUpdateNode } from 'shared/hooks/useMutation'

import screeningFormValidator, {
  ScreeningFormType,
  ScreeningAnswerType,
  ScreeningQuestionType,
  ScreeningParticipantType,
  getDummyQuestions,
  getEmptyScreeningForm,
  getEmptyParticipant,
} from 'shared/components/Screening/models/screening'

import useNoteActions from 'shared/components/Notes/useNoteActions'
import { getEmptyNote } from 'shared/components/Notes/models/note'

type FlaggedListProps = {
  baseRoute: string
}

type ShowFormDialogProps = {
  isPreview?: boolean
  continueLabel?: string
  cancelLabel?: string
}

export const PARENT_DIALOG = 'parent'
export const PARTICIPANT_DIALOG = 'participant'

type ScreeningActionsType = {
  onComplete?: () => void
}

export default function useScreeningActions(screeningParams?: ScreeningActionsType) {
  const dispatch = useDispatch()
  const { screening } = useSelector(screeningSelector)
  const { auth: user } = useSelector(authSelector)
  const { organization_information } = useSelector(organizationSettingSelector)
  const [, onCloseScreeningForm] = useMutation({
    url: 'note/daily/screening',
    method: 'POST',
  })

  const [, onFlagParticipantMutation] = useMutation({
    url: 'note/flag/screening',
    method: 'POST',
  })

  const screeningUsers = useMemo(() => {
    return screening?.users?.map((user: UserScreening) => ({
      ...getEmptyParticipant(),
      id: user?.user_id,
      showScreening: user?.showScreening,
      name: user?.name,
    }))
  }, [screening?.users])

  function handleClickInitialConfirmation(type: string, customScreening?: Screening, fromCheckin: boolean = false) {
    return (data?: ScreeningFormType) => {
      const customScreeningUsers = (customScreening?.users || []).map((user: UserScreening) => ({
        ...getEmptyParticipant(),
        id: user?.user_id,
        showScreening: user?.showScreening,
        name: user?.name,
      }))

      const participants = data?.participants?.map(participant => ({
        ...participant,
        answers: [],
        isSelected: false,
        question_count: 0,
      }))

      const users = customScreeningUsers?.length ? customScreeningUsers : participants || screeningUsers

      if (!fromCheckin) dispatch(hideDialog())

      if (organization_information.is_daily_screening_form) {
        dispatch(
          showDialog({
            component: ConfirmationDialog,
            props: {
              title: 'Daily Screening Form',
              continueLabel: 'Yes',
              cancelLabel: 'No',
              description: `
              ${organization_information.name}’s top priority is keeping all staff and members safe. We will require all members to fill out a Screening form on each day they are attending any in-person Program/Events at ${organization_information.name}.
              `,
              dialogClassName: 'note-dialog',
              screeningDate: customScreening?.date,
              async onValid() {
                if (type === PARTICIPANT_DIALOG) {
                  handleShowFormDialog(
                    {
                      isPreview: false,
                      continueLabel: 'Continue',
                    },
                    { participants: users },
                  )
                }

                if (type === PARENT_DIALOG) {
                  handleShowMultiUserFormDialog(
                    {
                      isPreview: false,
                      continueLabel: 'Continue',
                    },
                    { participants: users },
                  )
                }
              },
              onCancel() {
                handleDisregardForm()
              },
            },
          }),
        )
      }
    }
  }

  function handleDisregardForm() {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: BasicDialog,
        props: {
          title: 'Daily Screening Form',
          description: 'Thank you for letting us know.',
          dialogClassName: 'note-dialog note-dialog--no-footer',
          closeLabel: 'Go to Scoreboard',
          hasCancel: false,
          hasContinue: false,
          closeButton: true,
          async onValid() {},
          onCancel() {},
          onClose() {
            dispatch(hideDialog())
            onCloseScreeningForm({
              data: {
                user_id: user?.id,
              },
            })
          },
        },
      }),
    )
  }

  function handleShowFollowupDialog() {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: FollowupDialog,
        props: {
          title: 'Follow Up Required',
          dialogClassName: 'note-dialog note-dialog--no-footer',
          hasCancel: false,
          hasContinue: false,
          async onValid() {},
        },
      }),
    )
  }

  function handleFormSuccess() {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: BasicDialog,
        props: {
          title: 'Success!',
          description: `
            Your screening form has been successfully submitted and you are
            all clear to participate in programs at ${organization_information.name} today.
            We look forward to seeing them soon!.
          `,
          dialogClassName: 'note-dialog note-dialog--no-footer',
          closeLabel: 'Go to Scoreboard',
          hasCancel: false,
          hasContinue: false,
          closeButton: true,
          async onValid() {},
        },
      }),
    )
  }

  function handleShowFormDialog(props: ShowFormDialogProps, data?: ScreeningFormType) {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: FormDialog,
        props: {
          ...props,
          dialogClassName: 'note-dialog note-dialog--form',
          validator: screeningFormValidator,
          initialFields: {
            ...getEmptyScreeningForm(),
            ...(data ? data : {}),
          },
          async onValid(data: ScreeningFormType) {
            if (props?.isPreview) {
              const flagParticipants = data?.participants?.reduce(
                (records: any, participant: ScreeningParticipantType) => {
                  const hasYesParticipant = map(participant.answers, 'answer')
                    .map(each => upperCase(each))
                    .includes(upperCase('YES'))
                  return hasYesParticipant ? { ...records, [participant?.id]: true } : records
                },
                {},
              ) as any

              data?.participants?.forEach((participant: ScreeningParticipantType) => {
                participant?.showScreening &&
                  onCloseScreeningForm({
                    data: {
                      answers: participant?.answers,
                      user_id: participant?.id,
                      is_filled: true,
                    },
                  })

                Boolean(flagParticipants[participant?.id]) &&
                  participant?.showScreening &&
                  onFlagParticipantMutation({
                    data: { user_id: participant?.id },
                  })
              })

              screeningParams?.onComplete?.()
              return keys(flagParticipants).length ? handleShowFollowupDialog() : handleFormSuccess()
            } else {
              handleShowFormDialog(
                {
                  isPreview: true,
                  continueLabel: 'Submit Form',
                  cancelLabel: 'Make Changes',
                },
                data,
              )
            }
          },
          onCancel(event: React.MouseEvent<HTMLElement, MouseEvent>, cancelFields?: ScreeningFormType) {
            // dispatch(hideDialog())
            const hasAnswer = cancelFields?.participants?.some((participant: ScreeningParticipantType) => {
              return Boolean(participant.answers?.filter((each: ScreeningAnswerType) => Boolean(each?.answer)).length)
            })

            if (!props?.isPreview && hasAnswer) return handleCancelConfirmation(cancelFields)

            if (props?.isPreview) {
              return handleShowFormDialog(
                {
                  isPreview: false,
                  continueLabel: 'Continue',
                },
                data,
              )
            }

            handleClickInitialConfirmation(PARTICIPANT_DIALOG)(data)
          },
        },
      }),
    )
  }

  function handleShowMultiUserFormDialog(props: ShowFormDialogProps, data?: ScreeningFormType) {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: MultiUserFormDialog,
        props: {
          ...props,
          dialogClassName: 'note-dialog note-dialog--form note-dialog--no-footer',
          validator: screeningFormValidator,
          initialFields: {
            ...getEmptyScreeningForm(),
            ...(data ? data : {}),
          },
          async onValid(data: ScreeningFormType) {
            if (props?.isPreview) {
              const flagParticipants = data?.participants?.reduce(
                (records: any, participant: ScreeningParticipantType) => {
                  const hasYesParticipant = map(participant.answers, 'answer')
                    .map(each => upperCase(each))
                    .includes(upperCase('YES'))
                  return hasYesParticipant ? { ...records, [participant?.id]: true } : records
                },
                {},
              ) as any

              data?.participants?.forEach((participant: ScreeningParticipantType) => {
                const enable = participant?.isSelected && participant?.showScreening
                enable &&
                  onCloseScreeningForm({
                    data: {
                      answers: participant?.answers,
                      user_id: participant?.id,
                      is_filled: true,
                    },
                  })

                Boolean(flagParticipants[participant?.id]) &&
                  enable &&
                  onFlagParticipantMutation({
                    data: { user_id: participant?.id },
                  })
              })

              screeningParams?.onComplete?.()
              return keys(flagParticipants).length ? handleShowFollowupDialog() : handleFormSuccess()
            } else {
              handleShowMultiUserFormDialog(
                {
                  isPreview: true,
                  continueLabel: 'Submit Form',
                  cancelLabel: 'Make Changes',
                },
                data,
              )
            }
          },
          onCancel(event: React.MouseEvent<HTMLElement, MouseEvent>, cancelFields?: ScreeningFormType) {
            // dispatch(hideDialog())
            const hasAnswer = cancelFields?.participants?.some((participant: ScreeningParticipantType) => {
              return Boolean(participant.answers?.filter((each: ScreeningAnswerType) => Boolean(each?.answer)).length)
            })

            if (!props?.isPreview && hasAnswer) return handleCancelConfirmation(cancelFields, PARENT_DIALOG)

            if (props?.isPreview) {
              return handleShowMultiUserFormDialog(
                {
                  isPreview: false,
                  continueLabel: 'Continue',
                },
                data,
              )
            }

            handleClickInitialConfirmation(PARENT_DIALOG)()
          },
        },
      }),
    )
  }

  function handleCancelConfirmation(data?: ScreeningFormType, type: string = PARTICIPANT_DIALOG) {
    dispatch(hideDialog())
    dispatch(
      showDialog({
        component: ConfirmationDialog,
        props: {
          title: 'Are you sure you want to cancel?',
          continueLabel: 'No',
          cancelLabel: 'Yes',
          isSecondary: false,
          message: `Are you sure you want to cancel without submitting the form?`,
          description: `
            In order for your child to attend their Program/Event at ${organization_information.name} today, they have to complete a Screening form.
          `,
          dialogClassName: 'note-dialog',
          async onValid() {
            if (type === PARTICIPANT_DIALOG) {
              handleShowFormDialog(
                {
                  isPreview: false,
                  continueLabel: 'Continue',
                },
                data,
              )
            }

            if (type === PARENT_DIALOG) {
              handleShowMultiUserFormDialog(
                {
                  isPreview: false,
                  continueLabel: 'Continue',
                },
                data,
              )
            }
          },
          onCancel() {
            handleClickInitialConfirmation(type)(data)
          },
        },
      }),
    )
  }

  return {
    handleFormSuccess,
    handleShowFormDialog,
    handleDisregardForm,
    handleShowFollowupDialog,
    handleCancelConfirmation,
    handleShowMultiUserFormDialog,
    handleClickInitialConfirmation,
  }
}
